/**
 * Routing.
 *
 * Two shapes of route, decided by the hostname:
 *  - the bare platform domain is the DIRECTORY — discovery, artists, account
 *  - a festival subdomain is that FESTIVAL — its own lineup, map, plan
 *
 * The admin is lazy-loaded, so a visitor never downloads a CMS they will
 * never open.
 */
import { AfterRave } from './AfterRave';
import { OfflineToRave } from './OfflineToRave';
import { SetPlayerProvider } from '@/visitor/SetPlayer';
import { AppUpdate } from './AppUpdate';
import { RaveOffline } from '@/offline/RaveOffline';
import { SameDates } from './SameDates';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { TenantProvider, TenantWithEdition, useTenant } from './TenantProvider';
import { AuthProvider, useAuth } from './AuthProvider';
import { NativeSession } from './AppLaunch';
import { warmBundle } from '@/offline/bundle';
import { endpoints, startEarlyRefresh } from '@/lib/api';
import { earnDataPass } from '@/lib/dataPass';
import { signedInHere } from '@/lib/signOut';
import { LoadingLines } from './loadingLines';
import { SignInOutcome } from './SignInOutcome';
import { CompleteAccount } from './CompleteAccount';
import { RequireApproval } from './AwaitingApproval';
import { RequireMfa } from './RequireMfa';
import { WrongOrigin, originCannotHoldSession } from './WrongOrigin';
import { LocaleProvider } from './LocaleProvider';
import { useT } from '@/i18n';
import { EditionProvider } from './EditionProvider';
import { ErrorState, LoadingScreen, ToastProvider } from '@/ui';
import { isFramed } from '@/lib/framed';
import { PublicChrome } from './PlatformChrome';
import { HeaderBrand } from './BrandFlight';
import { AppShell } from './AppShell';
import { isAccountHost, isAppHost, isConsoleHost, isLoginHost, isOpsHost, isRaveAdminHost, isSupportHost, platformUrl, resolveTenant, supportUrl } from '@/lib/tenant';
import { RequireSession } from './RequireSession';
import { ToApp } from './ToApp';
import { ReturnAfterSignIn } from './ReturnAfterSignIn';
import { InviteTracking } from './InviteTracking';
import { InviteFriendProvider } from './InviteFriend';
import { inviteQuery } from '@/lib/inviteTracking';
import { enterPreviewScreen, inPreview } from '@/lib/preview';
import { ReadOnly } from '@/visitor/ReadOnly';
import { AccountLocale } from './LanguageSettings';
import { ScrollToTop } from './ScrollToTop';
import { LanguageAlternates } from './languageAlternates';
import { EnvironmentRibbon } from './EnvironmentRibbon';

/*
 * EVERY host's screens are lazy, not only the admin's (step 14, 2026-10-05).
 *
 * The entry file carried the login page, the account, the public site, the
 * dashboard and the whole rave app to every host — 806 kB, double what it was
 * in September — and Lighthouse put a first visit at 67 (a rave) and 82 (login)
 * against a budget of 90. Each host now downloads its own screens, and
 * `prefetchThisHost` starts that download while this module is still running,
 * so splitting adds no wait of its own.
 *
 * Offline is unaffected: the service worker precaches every visitor chunk the
 * build's graph reaches (`offline/swPlugin.ts` skips only `src/admin/`).
 */
const loaders = {
  festival: () => import('@/visitor/FestivalApp'),
  login: () => import('./LoginPage'),
  account: () => import('./AccountPage'),
  publicSite: () => import('./PublicSite'),
  dashboard: () => import('@/visitor/Dashboard'),
  directory: () => import('@/visitor/Directory'),
  artist: () => import('@/visitor/ArtistPage'),
  claimArtist: () => import('@/visitor/ClaimArtist'),
  artists: () => import('@/visitor/ArtistsIndex'),
  reset: () => import('@/visitor/Account'),
  invite: () => import('@/visitor/ArtistInvite'),
  news: () => import('./MyRaveNews'),
  messages: () => import('./Messages'),
  plans: () => import('./Plans'),
  adminInvitation: () => import('./AdminInvitation'),
  approveSignIn: () => import('./ApproveSignIn'),
  support: () => import('./Support'),
  raveWelcome: () => import('@/visitor/RaveWelcome'),
  completeProfile: () => import('./CompleteProfilePage'),
  tickets: () => import('./PlatformTickets'),
  ops: () => import('@/ops/OpsDashboard'),
};
const FestivalApp = lazy(() => loaders.festival().then((m) => ({ default: m.FestivalApp })));
const LoginPage = lazy(() => loaders.login().then((m) => ({ default: m.LoginPage })));
const AccountPage = lazy(() => loaders.account().then((m) => ({ default: m.AccountPage })));
const PublicSite = lazy(() => loaders.publicSite().then((m) => ({ default: m.PublicSite })));
const Dashboard = lazy(() => loaders.dashboard().then((m) => ({ default: m.Dashboard })));
const Directory = lazy(() => loaders.directory().then((m) => ({ default: m.Directory })));
const ArtistPage = lazy(() => loaders.artist().then((m) => ({ default: m.ArtistPage })));
const ToClaimTicket = lazy(() => loaders.claimArtist().then((m) => ({ default: m.ToClaimTicket })));
const PlatformArtists = lazy(() => loaders.artists().then((m) => ({ default: m.PlatformArtists })));
const ResetPassword = lazy(() => loaders.reset().then((m) => ({ default: m.ResetPassword })));
const ArtistInvite = lazy(() => loaders.invite().then((m) => ({ default: m.ArtistInvite })));
const PlatformTickets = lazy(() => loaders.tickets().then((m) => ({ default: m.PlatformTickets })));
const MyRaveNews = lazy(() => loaders.news().then((m) => ({ default: m.MyRaveNews })));
const Messages = lazy(() => loaders.messages().then((m) => ({ default: m.Messages })));
const PlansRoute = lazy(() => loaders.plans().then((m) => ({ default: m.PlansRoute })));
const AdminInvitation = lazy(() => loaders.adminInvitation().then((m) => ({ default: m.AdminInvitation })));
const ApproveSignIn = lazy(() => loaders.approveSignIn().then((m) => ({ default: m.ApproveSignIn })));
const SupportPage = lazy(() => loaders.support().then((m) => ({ default: m.Support })));
const OpsDashboard = lazy(() => loaders.ops().then((m) => ({ default: m.OpsDashboard })));
const PublicRavePage = lazy(() => loaders.raveWelcome().then((m) => ({ default: m.PublicRavePage })));
const PublicWhatsNew = lazy(() => loaders.news().then((m) => ({ default: m.PublicWhatsNew })));
const CompleteProfilePage = lazy(() => loaders.completeProfile().then((m) => ({ default: m.CompleteProfilePage })));

/** Start this host's own screen downloading now, beside the tenant lookup. */
export function prefetchThisHost(): void {
  if (typeof window === 'undefined') return;
  // The session too, beside the tenant lookup rather than after it — see
  // `startEarlyRefresh`. Not on an origin that cannot hold the session.
  // (Never under Vitest: importing `App` in a test must not send a request.)
  if (import.meta.env.MODE !== 'test' && !originCannotHoldSession()) startEarlyRefresh();
  // Step 99: signed out, earn the data pass at once, invisibly (`lib/dataPass.ts`) — the rave's
  // line-up asks for it a moment later. Never in a frame or a public preview (they read no rave).
  if (import.meta.env.MODE !== 'test' && !signedInHere() && !isFramed() && !inPreview()) void earnDataPass();
  const start = (load: () => Promise<unknown>) => { void load().catch(() => {}); };
  if (resolveTenant().subdomain !== null) {
    start(loaders.festival);
  }
  else if (isLoginHost()) {
    start(loaders.login);
    // The sign-in buttons too, so they are not what moves the card later.
    start(() => import('@/visitor/SignInOptions').then((m) => m.prefetchSignInOptions()));
  }
  else if (isAccountHost()) start(loaders.account);
  else if (isSupportHost()) start(loaders.support);
  else if (isOpsHost()) start(loaders.ops);
  else if (isAppHost()) start(loaders.dashboard);
  else if (!isConsoleHost() && !isRaveAdminHost()) start(loaders.publicSite);
}
prefetchThisHost();

/**
 * Start a rave's bundle the moment there is a session — beside the account
 * checks below it, not after them (step 14: refresh → completion → bundle ran
 * in series). Only for an approved account online; `refreshBundle` still makes
 * the real request if this one did not succeed.
 */
function WarmEdition() {
  const { tenant } = useTenant();
  const { user, offline } = useAuth();
  const editionId = tenant?.editionId;
  const ready = Boolean(editionId && user?.approved && !offline);
  useEffect(() => { if (ready && editionId) warmBundle(editionId); }, [ready, editionId]);
  return null;
}

/**
 * `app.…/invite?invite=<code>` — where the phone app opens a MyRave invite (`<platform>/app/invite`, the
 * Android app's claim; step 89, 2026-10-29): the front page, the invite carried (`InviteFriendProvider`
 * notes it there, counts the open and claims it once signed in).
 */
function MyRaveInviteLanding() {
  const { search } = useLocation();
  return <Navigate to={`/${inviteQuery(search)}`} replace />;
}

/** What a host shows while its own screen arrives. */
function Arriving() {
  const tr = useT();
  return <LoadingScreen label={tr('One moment…')} />;
}

/** The platform console is for a handful of people; nobody else downloads it. */
const Console = lazy(() => import('@/admin/console/Console').then((m) => ({ default: m.Console })));
/**
 * `<platform>/<rave>/<edition>` — the names from the address; anything else is the public site. So is
 * `<platform>/app/invite?invite=…`, a MyRave invite (`MYRAVE_INVITE_PATH`, 2026-10-29): two parts so the
 * Android app claims it, and `app` is reserved — never a rave.
 */
function PublicRaveRoute() {
  const { rave, edition } = useParams();
  if (!rave || !edition || !/^[a-z0-9-]{1,63}$/i.test(rave) || rave.toLowerCase() === 'app') return <PublicSite />;
  return <PublicRavePage rave={rave.toLowerCase()} edition={edition} />;
}

/**
 * The rave admin — every editor, the map canvas, the media uploader. It was
 * imported eagerly and so shipped inside the visitor bundle; a visitor never
 * opens `radm.` and should never download it.
 */
const RaveAdmin = lazy(() => import('@/admin/rave/RaveAdmin').then((m) => ({ default: m.RaveAdmin })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A festival lineup is not breaking news. Refetching on every window
      // focus is wasted bytes on the exact connection that cannot spare them.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 60_000,
    },
  },
});


  /*
   * The shell is ABOVE the edition fetch, and that ordering is the whole
   * point of it.
   *
   * `EditionProvider` holds its children back until the bundle lands — it has
   * to, because every screen under it reads acts and stages that do not exist
   * yet. With the shell inside it, that hold covered the chrome as well: a
   * person opening a rave got a bare "Loading the lineup…" with no header, no
   * menu and no way back to their other raves, and then the entire page at
   * once. Hoisted, the header and the menu are on screen immediately and only
   * the lineup waits. `FestivalApp` adds the rave's brand, countdown and
   * sections to that chrome through `useShellSlots` once it can.
   */
export function RaveHost({ editionId }: { editionId: string }) {
  /*
   * A public page's phone preview (`lib/preview.ts`): the rave app itself,
   * framed and read-only, signed out — no session gate, no welcome page.
   */
  if (inPreview()) {
    enterPreviewScreen();
    return (
      <ReadOnly>
        <AppShell>
          <EditionProvider editionId={editionId}>
            <Routes>
              <Route path="/*" element={<Suspense fallback={<Arriving />}><FestivalApp /></Suspense>} />
            </Routes>
          </EditionProvider>
        </AppShell>
      </ReadOnly>
    );
  }
  /*
   * A rave's address is the rave itself, signed out too — on the phone (the user, 2026-10-26:
   * "the app will not require a login at the opening") and on the WEB since 2026-10-27, when the
   * public page moved to the main domain (`<platform>/<rave>/<edition>`, `PublicRavePage`).
   */
  return (
    <>
    {/* `?invite=` — the open, and the claim once signed in (invite tracking, 2026-10-15). */}
    <InviteTracking editionId={editionId} />
    <RequireSession open>
      <AppShell>
        <EditionProvider editionId={editionId}>
          <Routes>
            <Route path="/*" element={<Suspense fallback={<Arriving />}><FestivalApp /></Suspense>} />
          </Routes>
        </EditionProvider>
      </AppShell>
    </RequireSession>
    </>
  );
}

/**
 * An invitation to run a rave (step 64.1) on `app.` — signed in or NOT.
 *
 * The email's link lands here before anybody has signed in, so it cannot sit
 * behind `RequireSession` (which would send them to `login.` and lose the
 * page). Signed in, it wears the app's shell like every `app.` page; signed
 * out, it stands alone and offers the sign-in itself.
 */
function AdminInvitationHost() {
  const { user, loading } = useAuth();
  if (loading) return <Arriving />;
  const page = <Suspense fallback={<Arriving />}><AdminInvitation /></Suspense>;
  return user ? <AppShell>{page}</AppShell> : page;
}

/**
 * Support (round 5) on `support.` — signed in or NOT, like an invitation: a person
 * who cannot sign in is the first who needs to write. Signed in and let in, it
 * wears the app's shell; signed out, the public site's chrome (whose footer
 * links here); an account still on the guest list gets the page alone — the
 * shell's own reads would all be refused.
 */
function SupportHost() {
  const { user, loading } = useAuth();
  const tr = useT();
  if (loading) return <Arriving />;
  const page = <Suspense fallback={<Arriving />}><SupportPage /></Suspense>;
  // "MyRave.io | Support Center" (the user, 2026-10-21) — signed in, the
  // user console's header says the same (`UserShell`).
  const brand = (
    <span className="shell-crumbs">
      <HeaderBrand href={platformUrl()} />
      <span aria-hidden="true" className="shell-crumb-rule" />
      <span className="shell-crumb">{tr('Support Center')}</span>
    </span>
  );
  if (!user) return <PublicChrome brand={brand}>{page}</PublicChrome>;
  return user.approved || user.role === 'SUPER_ADMIN' ? <AppShell>{page}</AppShell> : page;
}

/** Leave for another host (the old `app.…/support` → `support.`), replacing this entry in the history. */
function GoTo({ url }: { url: string }) {
  useEffect(() => { window.location.replace(url); }, [url]);
  return <Arriving />;
}

/**
 * The first path segment, when it is NOT one of the festival's published
 * years — the only shape a draft's address can have (`<sub>.<platform>/2027`).
 * The rave app's own screens live in the hash, so a festival host's path is
 * either empty, a year, or one of the two token pages handled above.
 */
function unpublishedSlug(published: readonly string[]): string | null {
  const first = window.location.pathname.split('/').filter(Boolean)[0];
  if (!first) return null;
  let segment: string;
  try { segment = decodeURIComponent(first); } catch { return null; }
  return published.includes(segment) ? null : segment;
}

/**
 * A DRAFT edition, opened by its address — for that rave's own admins only
 * (round 6: the setup wizard's preview is the rave app itself, framed).
 *
 * `/tenant` lists published editions only, so the draft is asked for by slug
 * (`GET /editions/:slug`, from this rave's origin); the API answers its
 * admins, content managers and platform admins and is a 404 to everyone else
 * — the same 404 the bundle gives them. On a 404, no session or no signal,
 * the address behaves exactly as before: the published year, or the main app.
 */
function DraftRave({ slug }: { slug: string }) {
  const { tenant } = useTenant();
  const { user, loading } = useAuth();
  const draft = useQuery({
    queryKey: ['edition-by-slug', tenant?.festivalSlug, slug],
    queryFn: () => endpoints.editionBySlug(slug),
    enabled: Boolean(user),
    retry: false,
    staleTime: 60_000,
  });
  if (loading || (user && draft.isLoading)) return <Arriving />;
  // The wizard's phone preview of a rave with no published year (staging, 2026-10-15: it showed
  // My raves): in the frame, the main app is never the answer — it waits for the session it
  // borrows from the wizard, and a draft it cannot read says so, with a way to try again.
  const inWizard = isFramed() && !tenant?.editionId;
  if (inWizard && !user) return <Arriving />;
  if (inWizard && !draft.data) return <PreviewFailed retry={() => void draft.refetch()} />;
  if (draft.data) {
    return (
      <TenantWithEdition edition={draft.data}>
        <RaveHost editionId={draft.data.id} />
      </TenantWithEdition>
    );
  }
  return tenant?.editionId ? <RaveHost editionId={tenant.editionId} /> : <ToApp />;
}

/**
 * A rave the public may not open (`tenant.hidden`: private — the user, 2026-11-02: "the domain should not work until
 * it is published, and don't show the loading screen of the rave at all"). Signed out: the main app at once. Signed
 * in, the API decides (`GET /editions/:id` answers its admins and platform admins, a 404 to everybody else) — and
 * while it does, MyRave's own "One moment…", never the rave's loading screen.
 */
export function HiddenRave() {
  const { tenant } = useTenant();
  const { user, loading } = useAuth();
  const slug = tenant?.editionSlug ?? null;
  const staff = useQuery({
    queryKey: ['edition-by-slug', tenant?.festivalSlug, slug],
    queryFn: () => endpoints.editionBySlug(slug!),
    enabled: Boolean(user) && Boolean(slug),
    retry: false,
    staleTime: 60_000,
  });
  if (loading || (user && slug && staff.isLoading)) return <Arriving />;
  return staff.data && tenant?.editionId ? <RaveHost editionId={tenant.editionId} /> : <ToApp />;
}

/** The wizard's preview could not read the draft it frames. */
function PreviewFailed({ retry }: { retry: () => void }) {
  const tr = useT();
  return <ErrorState title={tr("Couldn't open the preview")} detail={tr('Check the connection, then try again.')} retry={retry} retryLabel={tr('Try again')} />;
}

/** Exported for `hostRouting.test.tsx`: which host renders which screen. */
export function Shell() {
  const tr = useT();
  const { tenant, scope } = useTenant();

  // The reset link lands here on whatever host sent it, and must work before
  // any edition has loaded — someone locked out is not browsing a lineup.
  if (window.location.pathname === '/reset-password') {
    return <ResetPassword />;
  }
  // An artist invitation's link, the same way: it arrives from an email with a
  // one-time token and nothing behind it.
  if (window.location.pathname === '/artist-invite') {
    return <ArtistInvite />;
  }

  /*
   * A festival subdomain needs a session too, from 2026-09-24. It used to be
   * open on purpose — somebody handed a flyer with `ozora.myrave.io` on it was
   * SENT here rather than browsing — and that is the decision this reverses.
   * See `RequireSession`.
   */
  /*
   * A rave opens INSIDE the app, not beside it.
   *
   * It used to render its own shell — its own bar, its own hamburger, its own
   * account control — so a person inside a rave had left MyRave as far as the
   * chrome was concerned, with no way back to their other raves. Now it is the
   * same header and the same menu, extended: the platform's brand, then the
   * rave's, then the rave's own sections below the platform's in one drawer.
   */
  // An address naming no PUBLISHED year may name a draft its admin is
  // building — the setup wizard's phone preview frames exactly that address.
  const named = scope === 'festival' && tenant ? unpublishedSlug((tenant.editions ?? []).map((e) => e.slug)) : null;
  if (named) return <DraftRave slug={named} />;

  // Private, or nothing published: the main app, never the rave's loading screen — unless the API says this
  // person runs it (2026-11-02). The wizard's framed preview of a draft has its own address (`DraftRave`).
  if (scope === 'festival' && tenant?.hidden && !isFramed()) return <HiddenRave />;

  if (scope === 'festival' && tenant?.editionId) {
    return <RaveHost editionId={tenant.editionId} />;
  }

  // A festival with nothing published (or nothing this person may see) is not
  // a rave yet: off to the main app, not a dead end (the user, 2026-10-06).
  if (scope === 'festival') return <ToApp />;

  /*
   * The bare domain is the PUBLIC SITE; `app.` is the application.
   *
   * Both are `scope: 'platform'` — `app` is a reserved prefix and never a
   * festival — but they are two different products, and conflating them is
   * what put an account chip on a marketing page and the marketing page in
   * front of somebody who was already signed in. `PublicSite` sends a
   * signed-in visitor on to `app.` rather than showing them the pitch for a
   * thing they already use.
   */
  /*
   * `login.` is the sign-in host and nothing else. It is checked FIRST: it is
   * neither the public site nor the app, and it must not inherit either one's
   * chrome — a marketing nav above a password field is somewhere to go wrong.
   */
  if (isLoginHost()) return <LoginPage />;

  /*
   * `account.` — the account, on a host of its own.
   *
   * It wears the same shell as the app, because it is the same person doing
   * the same session's work: the header, the menu and the footer are all
   * still theirs. What it does NOT do is live inside `app.`'s route table,
   * so "where do I change my details" has an address somebody can say out
   * loud and a password manager can recognise.
   */
  /*
   * `radm.` — the rave admin (`plan.md` §65).
   *
   * Same shell as everything else: the operator is signed in as themselves,
   * and their raves, their account and their admin are three places in one
   * product rather than three products. The platform's own business —
   * approvals, sign-in methods, creating a rave — is `appadm.`'s.
   */
  /*
   * `appadm.` — the platform console (`plan.md` §66).
   *
   * Checked beside `radm.` and wearing the same shell, because they are the
   * same person doing two different jobs: one festival's, and the platform's.
   * The host decides where the screens live and nothing else — `Console`
   * shows a non-admin a sentence, and every route behind it checks
   * `SUPER_ADMIN` at the API, which is the part that actually holds.
   */
  if (isConsoleHost()) {
    return (
      <RequireSession>
        <AppShell>
          <Routes>
            <Route
              path="/*"
              element={(
                <Suspense fallback={<LoadingScreen label={tr('Loading the console…')} />}>
                  <Console />
                </Suspense>
              )}
            />
          </Routes>
        </AppShell>
      </RequireSession>
    );
  }

  if (isRaveAdminHost()) {
    return (
      <RequireSession>
        <AppShell>
          <Routes>
            <Route
              path="/*"
              element={(
                <Suspense fallback={<LoadingScreen label={tr('Loading the rave admin…')} />}>
                  <RaveAdmin />
                </Suspense>
              )}
            />
          </Routes>
        </AppShell>
      </RequireSession>
    );
  }

  if (isAccountHost()) {
    return (
      <RequireSession>
        <AppShell>
          <Routes>
            <Route path="/*" element={<Suspense fallback={<Arriving />}><AccountPage /></Suspense>} />
          </Routes>
        </AppShell>
      </RequireSession>
    );
  }

  /*
   * `support.` — the Support page, on a host of its own (the user,
   * 2026-10-21). Every path is the page: `?topic=` and `?rave=` say the rest.
   */
  if (isSupportHost()) return <SupportHost />;

  // `ops.` — the live dashboard (step 97): one full screen, no shell; its wall key or a platform admin's session.
  if (isOpsHost()) return <Suspense fallback={<Arriving />}><OpsDashboard /></Suspense>;

  if (!isAppHost()) {
    return (
      <Routes>
        {/* An artist page on the PUBLIC host wears the public chrome. It is
            reached by a shared link, so whoever lands on it may have no
            account — the app shell would show them an account menu with
            nobody in it. */}
        <Route path="/artists/:slug" element={<PublicChrome><ArtistPage /></PublicChrome>} />
        {/* The platform's release notes (step 54) — public, compiled in, offline. */}
        <Route path="/whats-new" element={<Suspense fallback={<Arriving />}><PublicWhatsNew /></Suspense>} />
        {/* A rave's public page — what an invite shares (2026-10-27): <platform>/<rave>/<edition>. */}
        <Route path="/:rave/:edition" element={<Suspense fallback={<Arriving />}><PublicRaveRoute /></Suspense>} />
          <Route path="*" element={<PublicSite />} />
      </Routes>
    );
  }

  if (window.location.pathname === '/admin-invitation') return <AdminInvitationHost />;
  // The old address, `app.…/support`, still works: it goes to `support.`.
  if (window.location.pathname === '/support') return <GoTo url={supportUrl(`/${window.location.search}`)} />;

  /*
   * The app host.
   *
   * **The guard and the shell wrap the ROUTES, not each route.** Two reasons,
   * and the second is the one that was a bug:
   *
   *  - A route added later is gated and framed by existing, rather than by
   *    somebody remembering to wrap it. That is the §4.0 shape avoided.
   *  - Written per-route, React unmounted and rebuilt `AppShell` on every
   *    navigation — so the menu, the search box and every other piece of
   *    chrome state was thrown away and rebuilt between pages. Hoisted, the
   *    shell stays mounted and only the content inside it changes.
   */
  return (
    <Routes>
      <Route
        path="*"
        element={
          // Open signed out (the user, 2026-10-26): Find a rave, a rave's page, an artist and
          // What's new need no account; what is somebody's own asks for one, route by route.
          <RequireSession open>
            <AppShell>
              <Suspense fallback={<Arriving />}>
              <Routes>
                {/*
                  * `/` is the DASHBOARD, not the directory.
                  *
                  * Somebody signed in has already chosen their raves; landing
                  * them on a search page makes them navigate to the thing they
                  * came for. Finding a rave is a real need, but a rarer one.
                  */}
                {/* The first page is Upcoming raves, for everybody, phone and website (2026-10-27). */}
                <Route path="/" element={<Directory page="upcoming" />} />
                {/* Find a rave — every rave, with its filters — is for an account (2026-10-27). */}
                <Route path="/raves" element={<RequireSession><Directory page="all" /></RequireSession>} />
                {/* My raves, on the phone and the website — `/` is Upcoming raves (2026-10-27). */}
                <Route path="/my-raves" element={<RequireSession><Dashboard /></RequireSession>} />
                {/* Every artist of an announced rave, by style (step 76) — browsed with no account. */}
                <Route path="/artists" element={<PlatformArtists />} />
                <Route path="/artists/:slug" element={<ArtistPage />} />
                {/* Claiming the page with proof — its own page, through the Support Center (2026-10-30). */}
                {/* The claim is a Support Center ticket now (the 0.14.74 walk): an old link goes there. */}
                <Route path="/artists/:slug/claim" element={<ToClaimTicket />} />
                {/* The platform's release notes, in the app's own shell and session. */}
                <Route path="/whats-new" element={<MyRaveNews />} />
                {/* My tickets, the phone app's bar (2026-10-29): signed out, the tickets' sign-in page. */}
                <Route path="/tickets" element={<PlatformTickets />} />
                {/* Every conversation — dating matches from every rave and ride
                    chats — lives here, not in a rave (the user, 2026-10-07, C6). */}
                <Route path="/messages" element={<RequireSession><Messages /></RequireSession>} />
                {/* The plans and the pack — what to buy, and what you have
                    (the user, 2026-10-08: "a page for subscription buy").
                    In the menu, and where a rave's paywall sends you. */}
                <Route path="/plans" element={<RequireSession><PlansRoute /></RequireSession>} />
                {/* Back here after the sign-in an invitation asked for (step 64.1). */}
                <Route path="/admin-invitation" element={<RequireSession><AdminInvitation /></RequireSession>} />
                {/* The phone approving a computer's sign-in with its passkey — the QR's address (2026-11-02). */}
                <Route path="/approve-sign-in" element={<RequireSession><ApproveSignIn /></RequireSession>} />
                {/* "Complete your profile" — every part optional; the banner on
                    My raves and Find a rave leads here (the user, 2026-10-20). */}
                <Route path="/complete" element={<RequireSession><CompleteProfilePage /></RequireSession>} />
                {/* A MyRave invite the phone app opened (step 89): the front page, with the invite. */}
                <Route path="/invite" element={<MyRaveInviteLanding />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
              {/* Back to `/plans` after a sign-in that it asked for. */}
              <ReturnAfterSignIn />
            </AppShell>
          </RequireSession>
        }
      />
    </Routes>
  );
}


export function App() {
  /*
   * **Before anything else, including the router.** An origin that cannot carry
   * the session cookie makes every screen below behave as though nobody is
   * signed in — and the 401 that produces reads exactly like a refused
   * password. Checked once, at the top, so no screen has to know about it.
   */
  if (originCannotHoldSession()) return <WrongOrigin />;

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {/* A new page opens at its top (the user, 2026-10-29). */}
        <ScrollToTop />
        {/* Above everything that renders words — the tenant lookup's own
            wait and error screens included — so `dir` is set on <html>
            before anything renders, and every screen speaks one language. A
            page that reflows after first paint is a page that flashes the
            wrong way round. */}
        <LocaleProvider>
        {/* Every language's address on a public page, and `?lang=` carried on its links (step 73.11). */}
        <LanguageAlternates />
        {/* DEV / STAGE on every screen of every host; nothing on production (step 96). */}
        <EnvironmentRibbon />
        {/* The lines every loading screen types out — inside the locale, which picks their language. */}
        <LoadingLines>
        {/* One toast stack for every host, inside the locale so its ✕ is translated. */}
        <ToastProvider>
        {/* The full-set player at the bottom of every page — a set keeps playing across screens (2026-11-02). */}
        <SetPlayerProvider>
        <TenantProvider>
          {/* Outside TenantProvider's data, inside the router: the session is
              the same on the directory and on every festival subdomain. */}
          <AuthProvider>
              <WarmEdition />
              {/* The account's language and region, on every device it signs in on (step 73). */}
              <AccountLocale />
              {/* The phone app's shell hears who is signed in — it reopens the last rave only for them. */}
              <NativeSession />
              {/* No signal in the main app: into the active rave — only a rave works offline (2026-10-30). */}
              <OfflineToRave />
              {/* The phone app: "A fresh MyRave just dropped" when a newer build is out (2026-10-30). */}
              <AppUpdate />
              {/* A rave's address keeps the app for offline, by the raves' dates (2026-10-30). */}
              <RaveOffline />
              {/* Raves on the same dates: pick the one you're going to (2026-10-30). */}
              <SameDates />
              {/* After the rave (step 108): the review and donate pushes open here (`?ask=review`, `?donate=1`). */}
              <AfterRave />
              {/* Above the shell, because coming back from a provider with
                  nothing to show is a whole screen and not a banner over an
                  app the person cannot use yet. */}
              {/* Invite a friend (step 89): the screen, its whole-hand swipe up, and MyRave's own invites. */}
              <InviteFriendProvider>
              <SignInOutcome>
                {/*
                  * **Completion OUTSIDE approval, and that order is the whole
                  * of `plan.md` §6.12.** It is titled "prove the contact
                  * BEFORE the approval queue, not after": an admin should not
                  * be spending a decision on an account nobody has checked is
                  * reachable, and the person should not be told to wait,
                  * approved, and then asked for something else.
                  *
                  * It only became possible when an unapproved account started
                  * holding a session — `/me/contact/start` needs one, so
                  * before §6.12 the person could not reach the gate that would
                  * ask them. Both gates are in the allow-list in
                  * `backend/src/http/approval.ts` for exactly that reason.
                  *
                  * Outside Shell, because an account that cannot use the app
                  * is a screen and not a banner over one. Anonymous visitors
                  * pass straight through both; most people here never make an
                  * account at all.
                  */}
                <CompleteAccount>
                  <RequireApproval>
                    {/* Step 70: an admin role passes its authenticator before the app. */}
                    <RequireMfa>
                    {/* The outer boundary for screens with no shell of their own
                        (login, the public site, reset, invite); a shell's screens
                        wait INSIDE it, so the header stays on screen. */}
                    <Suspense fallback={<Arriving />}>
                      <Shell />
                    </Suspense>
                    </RequireMfa>
                  </RequireApproval>
                </CompleteAccount>
              </SignInOutcome>
              </InviteFriendProvider>
          </AuthProvider>
        </TenantProvider>
        </SetPlayerProvider>
        </ToastProvider>
        </LoadingLines>
        </LocaleProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
